import { useNotes } from '@/context/NotesContext';
import { useFragments } from '@/context/FragmentsContext';
import { useTodos } from '@/context/TodoContext';
import { matchPath, useLocation } from 'react-router';
import { useEffect } from 'react';
import FragmentEditorModal from '../fragments/FragmentEditorModal';
import NoteEditorModal from '../notes/NoteEditorModal';
import NotesPanel from '../notes/NotesPanel';
import { ProjectDropdown } from '../projects/ProjectDropdown';
import { ProjectSelector } from '../projects/ProjectSelector';
import { TodosSidebar } from '../sidebar';
import BottomNavBar from './BottomNavBar';
import MainLogo from './MainLogo';
import NavBar from './NavBar';
import ReviewUIProviders from './ReviewUIProviders';
import { isReasonerAppPath } from '@/utils/reasonerRoute.utils';

/**
 * Routes where the persistent project selector strip is hidden.
 * - `/admin/*` covers the admin panel and all sub-routes.
 * - `/reDesign/:id/:section/*` hides the strip on workflow editing screens but
 *   keeps it on `/reDesign/mapping` (one segment), which needs the selector to
 *   say which project's schema is being mapped.
 * - `/reFocus/:id` hides the strip on a single dashboard view; `/reFocus/groups`
 *   is excluded explicitly so the list page keeps the selector.
 */
const HIDE_PROJECT_STRIP_PATTERNS = [
  '/admin/*',
  '/reDesign/:id/:section/*',
  '/projects',
  '/reFocus/:id',
];

const SHOW_PROJECT_STRIP_OVERRIDES = ['/reFocus/groups'];

/** Pages that manage their own internal scroll regions (no main-content scroll). */
const FIXED_HEIGHT_PAGE_PATHS = ['/reMap', '/reDesign/mapping'];

const isProjectStripHidden = (pathname: string) => {
  if (SHOW_PROJECT_STRIP_OVERRIDES.includes(pathname)) return false;
  return HIDE_PROJECT_STRIP_PATTERNS.some((pattern) =>
    matchPath(pattern, pathname)
  );
};

interface MainLayoutProps {
  children: React.ReactNode;
}

/** Inner layout: has access to Todo/Notes context; closes panels when leaving Reasoner route */
const MainLayoutContent = ({ children }: MainLayoutProps) => {
  const { pathname } = useLocation();
  const { setIsTodoPanelOpen } = useTodos();
  const { setIsNotesPanelOpen, setIsEditorModalOpen } = useNotes();
  const { setIsEditorModalOpen: setIsFragmentEditorOpen } = useFragments();

  useEffect(() => {
    const isReasonerRoute = isReasonerAppPath(pathname);
    if (!isReasonerRoute) {
      setIsTodoPanelOpen(false);
      setIsNotesPanelOpen(false);
      setIsEditorModalOpen(false);
      setIsFragmentEditorOpen(false);
    }
  }, [pathname, setIsTodoPanelOpen, setIsNotesPanelOpen, setIsEditorModalOpen, setIsFragmentEditorOpen]);

  const isFixedHeightPage = FIXED_HEIGHT_PAGE_PATHS.includes(pathname);

  return (
    <>
      <ProjectSelector />
      <div className='flex h-screen transition-colors bg-main-layout-bg dark:bg-main-layout-bg-dark text-foreground! dark:text-foreground-dark!'>
        <div className='w-16 bg-background dark:bg-background-dark flex flex-col items-center py-4'>
          <MainLogo />
          <NavBar />

          <div className='flex-1'></div>

          <BottomNavBar />
        </div>

        {/*
          main is a flex column so the project strip can claim its natural
          height (flex-shrink-0) and the page wrapper can take whatever's left
          (flex-1 min-h-0). Without this, children sized at `h-full` resolve
          to 100% of <main>, which already contains the strip as a sibling —
          so the page overflows by exactly the strip's height and the bottom
          gets pushed below the fold. Scroll lives on the inner wrapper now;
          components that need to scroll the canvas (e.g. scrollToBottom in
          DashboardChartsGridLayout) target [data-main-content-scroll].
        */}
        <main className='flex-1 m-4 rounded-md bg-background dark:bg-background-dark flex flex-col overflow-hidden'>
          {!isProjectStripHidden(pathname) && (
            <div className='border-b border-border px-4 py-2 shrink-0'>
              <div className='max-w-md'>
                <ProjectDropdown size='medium' />
              </div>
            </div>
          )}
          <div
            data-main-content-scroll=''
            className={`flex-1 min-h-0 ${isFixedHeightPage ? 'overflow-hidden' : 'overflow-auto'}`}
          >
            {children}
          </div>
        </main>
        <TodosSidebar />
        <NotesPanel />
      </div>
      <NoteEditorModal />
      <FragmentEditorModal />
    </>
  );
};

const MainLayout = ({ children }: MainLayoutProps) => {
  return (
    <ReviewUIProviders>
      <MainLayoutContent>{children}</MainLayoutContent>
    </ReviewUIProviders>
  );
};

export default MainLayout;
